import { type IconName, Icon } from "@mdotm/shadcn/legacy/components";
import { type ButtonPalette, Button, FormField } from "@mdotm/shadcn/mdotm/components";
import { Skeleton } from "@mdotm/shadcn/ui";
import { unreachable } from "@mdotm/utils/runtime";
import { type ReactNode, useState } from "react";

/** Whether the secret has a value: `checking` until that is known, `unknown` when it could not be found out. */
export type SecretStatus = "checking" | "set" | "unset" | "unknown";

export type SecretFieldProps = {
	label: string;
	status: SecretStatus;
	/** The admin chose to remove the value; it goes on the next save. */
	removing: boolean;
	/** The field's error, for the states that do not show the input (the input shows its own). */
	error?: string;
	/** The input that takes a new value. */
	children: ReactNode;
	onRemove(): void;
	onUndoRemove(): void;
	/** Drops what was typed into the input, so the saved value stays. */
	onCancelReplace(): void;
	onRetry?(): void;
	qualifier: string;
};

type SecretFieldMode = "loading" | "input" | "inputAfterFailedCheck" | "set" | "replacing" | "removing";

function secretFieldMode(status: SecretStatus, removing: boolean, replacing: boolean): SecretFieldMode {
	switch (status) {
		case "checking":
			return "loading";
		case "unset":
			return "input";
		case "unknown":
			return "inputAfterFailedCheck";
		case "set":
			if (removing) {
				return "removing";
			}
			return replacing ? "replacing" : "set";
		default:
			return unreachable(status);
	}
}

const MASK = "••••••••••••";

const maskedBoxClassName =
	"flex items-center gap-2 h-8 px-2 border border-[var(--palette-N200)] bg-[var(--palette-N50)] rounded-sm";

/** An icon-only action outside the field, as the dialog's logo boxes place their Delete. */
function SecretAction(props: { icon: IconName; palette: ButtonPalette; "aria-label": string; onClick(): void }) {
	return (
		<Button
			size="small"
			palette={props.palette}
			aria-label={props["aria-label"]}
			title={props["aria-label"]}
			onClick={props.onClick}
		>
			<Icon icon={props.icon} size={16} />
		</Button>
	);
}

/**
 * The field with its actions beside it, aligned with the field rather than with its label. Without actions the
 * field takes the whole width; with them the slot keeps one width, so the field does not move as the admin
 * replaces or removes the value.
 */
function BesideField(props: { children: ReactNode; actions?: ReactNode; qualifier: string }) {
	return (
		<div className="flex items-start gap-2" data-qualifier={props.qualifier}>
			<div className="flex-1 min-w-0">{props.children}</div>
			{props.actions && (
				<FormField label={<>&nbsp;</>} className="w-[76px] shrink-0">
					<div className="flex items-center gap-2 h-8">{props.actions}</div>
				</FormField>
			)}
		</div>
	);
}

/**
 * A write-only secret: once it has a value, the value is never shown again. The admin sees that it is set and
 * can replace it with a new one or remove it; a removal waits for the save and can be undone until then.
 */
export function SecretField({
	label,
	status,
	removing,
	error,
	children,
	onRemove,
	onUndoRemove,
	onCancelReplace,
	onRetry,
	qualifier,
}: SecretFieldProps) {
	const [replacing, setReplacing] = useState(false);
	const mode = secretFieldMode(status, removing, replacing);

	switch (mode) {
		case "loading":
			return (
				<BesideField qualifier={qualifier}>
					<FormField label={label}>
						<Skeleton className="h-8 w-full" aria-label={`Loading ${label}`} />
					</FormField>
				</BesideField>
			);
		case "input":
			return <BesideField qualifier={qualifier}>{children}</BesideField>;
		case "inputAfterFailedCheck":
			return (
				<BesideField
					qualifier={qualifier}
					actions={onRetry && <SecretAction icon="Reload" palette="secondary" aria-label="Retry" onClick={onRetry} />}
				>
					{children}
					<p className="mt-1">Could not check whether a value is set. A value you type replaces any saved one.</p>
				</BesideField>
			);
		case "replacing":
			return (
				<BesideField
					qualifier={qualifier}
					actions={
						<SecretAction
							icon="Close"
							palette="secondary"
							aria-label={`Cancel the new ${label}`}
							onClick={() => {
								onCancelReplace();
								setReplacing(false);
							}}
						/>
					}
				>
					{children}
				</BesideField>
			);
		case "set":
			return (
				<BesideField
					qualifier={qualifier}
					actions={
						<>
							<SecretAction
								icon="Edit"
								palette="primary"
								aria-label={`Replace ${label}`}
								onClick={() => setReplacing(true)}
							/>
							<SecretAction icon="Delete" palette="secondary" aria-label={`Remove ${label}`} onClick={onRemove} />
						</>
					}
				>
					<FormField label={label} error={error}>
						<div className={maskedBoxClassName}>
							<span className="tracking-widest" aria-hidden>
								{MASK}
							</span>
							<span className="sr-only">{label} is set</span>
						</div>
					</FormField>
				</BesideField>
			);
		case "removing":
			return (
				<BesideField
					qualifier={qualifier}
					actions={
						<SecretAction
							icon="restore"
							palette="secondary"
							aria-label={`Undo the removal of ${label}`}
							onClick={onUndoRemove}
						/>
					}
				>
					<FormField label={label} error={error}>
						<div className={maskedBoxClassName}>
							<span className="flex-1 line-through text-[var(--palette-N400)] tracking-widest" aria-hidden>
								{MASK}
							</span>
							<span className="text-[var(--palette-N500)]">Removed when you save</span>
						</div>
					</FormField>
				</BesideField>
			);
		default:
			return unreachable(mode);
	}
}
